import _, {
  filter, orderBy, find, compact, isDate,
} from 'lodash';
import {
  UnitsApi, Unit, User, UserApi, DocumentRequest, Documents,
  EventStatus, Event, PartnersApi, MarketsApi, Partner, Market,
} from '@compassdigital/foodworks.ts.sdk';
import Format from '@/util/Format';
import UserUtil from '@/util/UserUtil';
import UserModule from '@/store/modules/user';
import AppModule from '@/store/modules/app';
import SystemModule from '@/store/modules/system';
import MarketUtil from '@/util/MarketUtil';
import Service from '@/util/ServiceUtil';
import { IUserTableInfo } from '@/interface/userTableInfo';
import moment from 'moment';
import { IDocumentsInfo } from '@/interface/documentInfo';
import { ISchedulerTableInfo } from '@/interface/schedulerTableInfo';
import { IMarketOverviewTableInfo } from '@/interface/marketOverviewtTableInfo';
import PartnerUtil from './PartnerUtil';

export default class StatsUtil {
  /**
   * get the formatted user count
   @param users
   */
  public static getCountUsers(users: Array<User>,
    prevDate: string,
    nextDate: string,
    isDateRange: boolean) {
    const usersOnDate = isDateRange
      ? this.getDateRangeStats(users, prevDate, nextDate)
      : this.getDayWiseStats(users, prevDate);
    const fetchedUser = this.getMappedUsers(compact(usersOnDate));
    const newUpdateUser: User[] = compact(usersOnDate);
    const fwManagerCount = this.getFwManager(newUpdateUser);
    const adminCount = this.getAdminUser(newUpdateUser);
    const unitCount = this.getUnitManager(newUpdateUser);
    const partnerCount = this.getPartnerRole(newUpdateUser);
    const activeUsers = this.activeUser(newUpdateUser);
    const deativateUsers = this.deactivateUser(newUpdateUser);
    const invitedUser = this.invitedUsers(newUpdateUser);
    const mappedUsers: any = {};
    mappedUsers.fetchedUser = fetchedUser;
    mappedUsers.fwManagerCount = fwManagerCount.length;
    mappedUsers.adminCount = adminCount.length;
    mappedUsers.unitCount = unitCount.length;
    mappedUsers.partnerCount = partnerCount.length;
    mappedUsers.activeUsers = activeUsers.length;
    mappedUsers.deativateUsers = deativateUsers.length;
    mappedUsers.invitedUsers = invitedUser.length;
    mappedUsers.totalUsers = (compact(usersOnDate).length);
    return mappedUsers;
  }

  public static async getMappedDocs(docs: Array<Document>,
    prevDate: string,
    nextDate: string,
    isDateRange: boolean) {
    const docOnDate = isDateRange
      ? this.getDateRangeStats(docs, prevDate, nextDate)
      : this.getDayWiseStats(docs, prevDate);
    const mappedDoc: any = {};
    const fetchedDocs = await this.mappedDocs(compact(docOnDate));
    const uploadedDocs = this.getUploadedDocs(compact(fetchedDocs));
    mappedDoc.fetchedDocs = fetchedDocs;
    mappedDoc.countDocs = uploadedDocs;
    return mappedDoc;
  }

  public static getAdminUser(users: Array<User>) {
    const adminUser = !users || users.map((user) => {
      const { permissions } = user;
      if (permissions?.scopes) {
        const { scopes } = permissions;
        const scopeC = scopes[0];
        if (scopeC === 'fw_admin') {
          return user;
        }
        return '';
      }
      return '';
    });
    return orderBy((compact(adminUser)), '_createdDate', 'desc');
  }

  public static getFwManager(users: Array<User>) {
    const roleFwManager = !users || users.map((user) => {
      const { permissions } = user;
      if (permissions?.scopes) {
        const { scopes } = permissions;
        const scopeC = scopes[0];
        if (scopeC === 'fw_manager') {
          return user;
        }
        return '';
      }
      return '';
    });
    return orderBy((compact(roleFwManager)), '_createdDate', 'desc');
  }

  public static getUnitManager(users: Array<User>) {
    const roleUnitManager = !users || users.map((user) => {
      const { permissions } = user;
      if (permissions?.scopes) {
        const { scopes } = permissions;
        const scopeC = scopes[0];
        if (scopeC === 'unit_manager') {
          return user;
        }
        return '';
      }
      return '';
    });
    return orderBy((compact(roleUnitManager)), '_createdDate', 'desc');
  }

  public static getOnBoardingCounts(
    onboardStats: any,
    prevDate: string,
    nextDate: string,
    isDateRange: boolean,
  ) {
    const filteredOnboardStats = isDateRange
      ? this.getFilteredOnboardsByDate(onboardStats, prevDate, nextDate)
      : this.getDayWiseOnboards(onboardStats, prevDate);
    const today = this.getToday(onboardStats);
    const sevenDays = this.getSevenDays(onboardStats);
    const thirtyDays = this.getThirtyDays(onboardStats);
    const dateRange = this.getDateRange(onboardStats, prevDate, nextDate);

    const mappedOnboardStats: any = {
      partners: {
        today: today.partners,
        sevenDays: sevenDays.partners,
        thirtyDays: thirtyDays.partners,
        dateRange: dateRange.partners,
      },
      units: {
        today: today.units,
        sevenDays: sevenDays.units,
        thirtyDays: thirtyDays.units,
        dateRange: dateRange.units,
      },
      filteredOnboardStats,
    };

    return mappedOnboardStats;
  }

  public static getFilteredOnboardsByDate(onboardStats: any, prevDate: string, nextDate: string) {
    return {
      partners: this.getDateRangeStats(onboardStats.partners, prevDate, nextDate),
      units: this.getDateRangeStats(onboardStats.units, prevDate, nextDate),
    };
  }

  public static getDayWiseOnboards(onboardStats: any, prevDate: string) {
    return {
      partners: this.getDayWiseStats(onboardStats.partners, prevDate),
      units: this.getDayWiseStats(onboardStats.units, prevDate),
    };
  }

  public static getToday(arr: any) {
    const curDate = moment().format('YYYY-MM-DD');
    const onboardPartnerStatsOnDate = !arr || arr.partners.filter((onboard: any) => {
      const { _createdDate } = onboard;
      const date = moment(_createdDate).format('YYYY-MM-DD');
      if (curDate === date) {
        return onboard;
      }
      return null;
    });
    const onboardUnitStatsOnDate = !arr || arr.units.filter((onboard: any) => {
      const { _createdDate } = onboard;
      const date = moment(_createdDate).format('YYYY-MM-DD');
      if (curDate === date) { return onboard; }
      return null;
    });

    return {
      partners: onboardPartnerStatsOnDate,
      units: onboardUnitStatsOnDate,
    };
  }

  public static getDatewiseStats(arr: any, arrDate: string) {
    const curDate = moment().format('YYYY-MM-DD');
    const statsOnDate = !arr
      || arr.filter((stat: any) => {
        const { _createdDate } = stat;
        const date = moment(_createdDate).format('YYYY-MM-DD');
        if (moment(date).isBetween(arrDate, curDate, null!, '[]')) { return stat; }
        return null;
      });

    return statsOnDate.filter((el: any) => el);
  }

  public static getSevenDays(arr: any) {
    const date = moment().subtract(7, 'days').format('YYYY-MM-DD');
    const partners = this.getDatewiseStats(arr.partners, date);
    const units = this.getDatewiseStats(arr.units, date);
    return {
      partners,
      units,
    };
  }

  public static getThirtyDays(arr: any) {
    const date = moment().subtract(30, 'days').format('YYYY-MM-DD');
    const partners = this.getDatewiseStats(arr.partners, date);
    const units = this.getDatewiseStats(arr.units, date);
    return {
      partners,
      units,
    };
  }

  public static getDateRange(onboardStats: any, prevDate: string, nextDate: string) {
    return {
      partners: this.getDateRangeStats(onboardStats.partners, prevDate, nextDate),
      units: this.getDateRangeStats(onboardStats.units, prevDate, nextDate),
    };
  }

  public static getPartnerRole(users: Array<User>) {
    const rolePartner = !users || users.map((user) => {
      const { permissions } = user;
      if (permissions?.scopes) {
        const { scopes } = permissions;
        const scopeC = scopes[0];
        if (scopeC === 'partner_owner') {
          return user;
        }
        return '';
      }
      return '';
    });
    return orderBy((compact(rolePartner)), '_createdDate', 'desc');
  }

  public static activeUser(users: Array<User>) {
    const rolePartner = !users || users.map((user) => {
      const { is } = user;
      if (is?.disabled === false) {
        return user;
      }
      return '';
    });
    return orderBy((compact(rolePartner)), '_createdDate', 'desc');
  }

  public static deactivateUser(users: Array<User>) {
    const rolePartner = !users || users.map((user) => {
      const { is } = user;
      if (is?.disabled) {
        return user;
      }
      return '';
    });
    return orderBy((compact(rolePartner)), '_createdDate', 'desc');
  }

  public static invitedUsers(users: Array<User>) {
    const rolePartner = !users || users.map((user: any) => {
      const { is } = user;
      if (is?.new) {
        return user;
      }
      return '';
    });
    return orderBy((compact(rolePartner)), '_createdDate', 'desc');
  }

  /**
   * Parses the users and return formatted users
   * @param users
   */

  public static getMappedUsers(users: Array<User>): Array<IUserTableInfo> {
    const mappedUsers = !users
      || users.map((user: any) => {
        const {
          id, name, email, is, _createdDate, _lastUpdatedDate, markets,
        } = user;

        let userName = '';
        let disabled = 'Deactivated';
        if (is && is.disabled != null) {
          disabled = is.disabled === false ? 'Active' : 'Deactivated';
        }
        let avatar = '';
        if (name && name.first && name.last) {
          userName = Format.capitalize(`${name.first} ${name.last}`);
          avatar = name.first[0].toUpperCase() + name.last[0].toUpperCase();
        }
        const userRole = (user.permissions?.scopes[0]) || '';
        return {
          userId: id,
          userName,
          disabled,
          name,
          userRole,
          markets,
          userFormatedRole: UserUtil.getFormatedRole(userRole),
          userStatusColor: UserUtil.getUserStatusColor(disabled),
          _createdDate,
          _lastUpdatedDate,
        };
      });

    const { currentRole } = UserModule;
    if (currentRole === 'fw_manager') {
      const filteredUsers = mappedUsers
        .filter((user: IUserTableInfo) => user.userRole !== 'fw_admin');
      return orderBy(filteredUsers, '_createdDate', 'desc');
    }

    return orderBy(mappedUsers, '_createdDate', 'desc');
  }

  public static async acessMarkets() {
    if (UserModule.currentRole === 'fw_manager') {
      const { markets } = SystemModule;
      const api = new UserApi(AppModule.apiConfig);
      const access = await api.getUserAccessById((UserModule.currentUser?.id as string));
      const items = MarketUtil.getMappedMarkets(markets);
      const marketItems = orderBy(items, [(user: any) => user.text.toLowerCase()], 'asc');
      const marketaccess = access.data.marketId || [];
      const marketArray = marketItems.filter(e => marketaccess.includes(e.value));
      if (marketArray) {
        return marketArray;
      }
      return null;
    }

    if (UserModule.currentRole === 'fw_admin') {
      const { markets } = SystemModule;
      const items = MarketUtil.getMappedMarkets(markets);
      const marketItems = orderBy(items, [(user: any) => user.text.toLowerCase()], 'asc');
      if (marketItems) {
        return marketItems;
      }
      return null;
    }
    return null;
  }

  public static async getMappedPartnersUnits(partnersUnits: any) {
    const marketsApi = new MarketsApi(AppModule.apiConfig);
    const markets = await marketsApi.getMarkets();
    const mappedPartners = !partnersUnits
      || partnersUnits.partners.map((partner: any) => {
        const {
          id, name, onboarding, _createdDate, _lastUpdatedDate, marketId,
        } = partner;
        let market;
        if (marketId) {
          market = markets.data.find((p) => p.id === marketId);
        }

        const status = onboarding?.status;

        const statusColor = PartnerUtil.getColor(status as string);

        const partnerObj = {
          id,
          market,
          name,
          status: PartnerUtil.getStatus(status),
          date: Format.getFomattedDate(_createdDate),
          statusColor,
          _createdDate,
          _lastUpdatedDate,
        };
        return partnerObj;
      });

    const mappedUnits = !partnersUnits
      || partnersUnits.units.map((unit: any) => {
        const {
          id, _createdDate, _lastUpdatedDate, marketId,
        } = unit;
        let market;
        if (unit.marketId) {
          market = markets.data.find((p) => p.id === marketId);
        }

        const unitObj = {
          id,
          market,
          date: Format.getFomattedDate(_createdDate),
          unit,
          _createdDate,
          _lastUpdatedDate,
        };
        return unitObj;
      });


    const partners = orderBy(mappedPartners, '_createdDate', 'desc');

    const units = orderBy(mappedUnits, '_createdDate', 'desc');

    return {
      partners,
      units,
    };
  }

  public static async getMappedMarket(users: Array<User>) {
    const mappedUsers = !users
      || users.map(async (user: any) => {
        const {
          id, name, email, is, _createdDate, _lastUpdatedDate, permissions, phone,
        } = user;

        const userRole = (user.permissions?.scopes[0]) || '';
        let markets: any = [];
        if (userRole === 'fw_manager') {
          markets = await this.acessFWMarkets(id);
        }
        return {
          email,
          id,
          is,
          name,
          markets,
          permissions,
          phone,
          _createdDate,
          _lastUpdatedDate,
        };
      });
    return Promise.all(mappedUsers);
  }

  public static async acessFWMarkets(id: string) {
    const api = new UserApi(AppModule.apiConfig);
    let userMarket = (await api.getUserAccessById(id)).data.marketId || [];
    const systemMarketIds = new Set(SystemModule.markets.map(e => e.id as string));
    userMarket = userMarket.filter(e => systemMarketIds.has(e));
    if (userMarket) {
      return userMarket;
    }
    return null;
  }

  /**
   * Parses the documets and return formatted documents
   * @param documents
   */

  public static async mappedDocs(documents: Array<DocumentRequest>) {
    const partners = await this.getPartners();
    let filteredDocs = documents;
    if (UserModule.currentRole === 'partner_owner') {
      filteredDocs = !documents
      || documents.filter((doc: any) => doc.owneruserId === UserModule.currentPartner?.ownerUserId);
    }
    const docs = !filteredDocs
      || filteredDocs.map((doc: any) => {
        const {
          id, documentName, status, _createdDate,
          _lastUpdatedDate, type, marketId, partnerId,
          expiryDate,
        } = doc;
        let partner = null;
        if (UserModule.currentRole === 'fw_admin'
          || UserModule.currentRole === 'fw_manager'
          || UserModule.currentRole === 'partner_owner') {
          if (partners.filter((p: Partner) => p.id === partnerId).length > 0) {
            partner = { ...partners.filter((p: Partner) => p.id === partnerId)[0] };
          }
        }
        let docType: any = '';

        if (type) {
          docType = this.getFormattedType(type);
        }

        let dateFormatted = '';
        if (_createdDate) {
          dateFormatted = Format.getDateDocs(_createdDate);
        }

        return {
          id,
          docType,
          expiryDate,
          marketId,
          status,
          partner,
          documentName,
          _createdDate,
          _lastUpdatedDate,
          dateFormatted,
        };
      });
    return orderBy(docs, '_createdDate', 'desc');
  }

  public static getFormattedType(type?: string) {
    switch (type) {
      case 'safety-questionnaire':
        return 'Food Safety Questionnaire';
      case 'insurance':
        return 'Certificate of Insurance';
      case 'business-license':
        return 'Business License';
      case 'health-permit':
        return 'Health Permit';
      case 'health-inspection':
        return 'Health Inspection';
      case 'serve-safe':
        return 'ServSafe';
      case 'partner-agreement':
        return 'RF Agreement';
      case 'w-9':
        return 'W-9 Form';
      case 'allergen-management-certificate':
        return 'Allergen Management Certificate';
      case 'apron-exchange-partner-agreement':
        return 'Apron Exchange Partner Agreement';
      default:
        return null;
    }
  }

  public static getUploadedDocs(docs: Array<any>): IDocumentsInfo {
    const totalDocs = docs.length;
    const approvedDocs = docs.filter((doc: any) => doc.status === 'accepted');
    const rejectedDocs = docs.filter((doc: any) => doc.status === 'rejected');
    let noExpiry = 0;
    let expiringValue = 0;
    let expiredValue = 0;
    let noAssignedExp = 0;
    docs.forEach((doc: any) => {
      if (doc.expiryDate) {
        const expiredDoc: any = Service.getDocumentExpire(doc.expiryDate);
        if (expiredDoc < 60 && expiredDoc > 0) {
          expiringValue += 1;
        }
        if (expiredDoc === null) {
          expiredValue += 1;
        }
        if (expiredDoc >= 60) {
          noExpiry += 1;
        }
      }
      if (doc.expiryDate === undefined) {
        noAssignedExp += 1;
      }
      return '';
    });

    const countDoc: IDocumentsInfo = {};
    countDoc.approvedDocs = approvedDocs.length;
    countDoc.rejectedDocs = rejectedDocs.length;
    countDoc.totalDocs = totalDocs;
    countDoc.noExpiry = noExpiry + noAssignedExp;
    countDoc.expiringValue = expiringValue;
    countDoc.expiredValue = expiredValue;
    return countDoc;
  }

  /* Scheduler Statistics */

  public static getCountEvents(events: Array<ISchedulerTableInfo>,
    prevDate: string, nextDate: string, isDateRange: boolean) {
    const filteredEvents = isDateRange
      ? this.getDateRangeEvents(events, prevDate, nextDate)
      : this.getDayWiseEvents(events, prevDate);
    const accepted = this.getAcceptedEvents(filteredEvents);
    const rejected = this.getRejectedEvents(filteredEvents);
    const pending = this.getPendingEvents(filteredEvents);
    const cancelled = this.getCancelledEvents(filteredEvents);
    const noResponse = this.getNoResponseEvents(filteredEvents);
    const noShow = this.getNoShowEvents(filteredEvents);
    const averageResponseTimeEvents = this.getAverageResponseTime(filteredEvents);
    const mappedEvents: any = {};
    mappedEvents.filteredEvents = filteredEvents;
    mappedEvents.acceptedEvents = accepted.length;
    mappedEvents.rejectedEvents = rejected.length;
    mappedEvents.pendingEvents = pending.length;
    mappedEvents.cancelledEvents = cancelled.length;
    mappedEvents.noResponseEvents = noResponse.length;
    mappedEvents.averageResponseTime = averageResponseTimeEvents;
    mappedEvents.noShowEvents = noShow.length;
    return mappedEvents;
  }

  public static getAverageResponseTime(events: Array<ISchedulerTableInfo>) {
    const acceptedDeclinedEvents = events.filter((el: any) => el.status === EventStatus.Accepted
      || el.status === EventStatus.Declined || el.status === 'live');
    const averageResponseHours = acceptedDeclinedEvents.map((el: any) => {
      const { _createdDate, _lastUpdatedDate } = el;
      const lastUpdatedDate = moment(new Date(_lastUpdatedDate), 'DD/MM/YYYY HH:mm:ss');
      const createdDate = moment(new Date(_createdDate), 'DD/MM/YYYY HH:mm:ss');
      const timeDuration = moment.duration(lastUpdatedDate.diff(createdDate)).asSeconds();
      return timeDuration;
    });
    let avg = 0;
    let sum = 0;
    averageResponseHours.forEach((el: any) => {
      if (!Number.isNaN(el)) {
        sum += parseInt(el, 10);
      }
    });
    avg = sum / averageResponseHours.length;
    return Number.isNaN(avg) ? 0 : avg;
  }

  public static getAcceptedEvents(events: Array<ISchedulerTableInfo>):
    Array<ISchedulerTableInfo> {
    return events.filter((el: any) => el.status === EventStatus.Accepted
      || el.status === 'live');
  }

  public static getRejectedEvents(events: Array<ISchedulerTableInfo>):
    Array<ISchedulerTableInfo> {
    return events.filter((el: any) => el.status === EventStatus.Declined);
  }

  public static getPendingEvents(events: Array<ISchedulerTableInfo>):
    Array<ISchedulerTableInfo> {
    return events.filter((el: any) => el.status === EventStatus.Pending);
  }

  public static getNoResponseEvents(events: Array<ISchedulerTableInfo>):
    Array<ISchedulerTableInfo> {
    return events.filter((el: any) => el.status === EventStatus.NoResponse);
  }

  public static getCancelledEvents(events: Array<ISchedulerTableInfo>):
    Array<ISchedulerTableInfo> {
    return events.filter((el: any) => el.status === EventStatus.Canceled);
  }

  public static getNoShowEvents(events: Array<ISchedulerTableInfo>):
    Array<ISchedulerTableInfo> {
    return events.filter((el: any) => el.status === EventStatus.NoShow);
  }

  public static getFilteredEventsByStatus(event: ISchedulerTableInfo) {
    switch (event.status) {
      case 'live':
        return true;
      case EventStatus.Accepted:
        return true;
      case EventStatus.Declined:
        return true;
      case EventStatus.NoResponse:
        return true;
      case EventStatus.Pending:
        return true;
      case EventStatus.Canceled:
        return true;
      case EventStatus.NoShow:
        return true;
      default:
        return false;
    }
  }

  public static getFilteredEventsTableByStatus(event: ISchedulerTableInfo) {
    if (event.status === EventStatus.Accepted || event.status === EventStatus.NoResponse
      || event.status === EventStatus.Pending || event.status === EventStatus.Canceled
      || event.status === EventStatus.Declined || event.status === 'live'
      || event.status === EventStatus.NoShow) {
      return true;
    }
    return false;
  }

  public static async getMappedEvents(events: Array<Event>):
    Promise<Array<ISchedulerTableInfo>> {
    const marketsApi = new MarketsApi(AppModule.apiConfig);
    const partners = await this.getPartners();
    const units = await this.getUnits();
    const markets = await marketsApi.getMarkets();

    const eventsFiltered = !events || events.map(event => {
      if (this.getFilteredEventsByStatus(event)) {
        return event;
      }
      return '';
    });

    const mappedEvents = !eventsFiltered
      || eventsFiltered.map((event: any) => {
        const {
          id, _createdDate, _lastUpdatedDate, status, date,
        } = event;
        let partner: Partner | undefined;
        let market;
        let unit;
        if (event.partnerId) {
          partner = partners.find((p: Partner) => p.id === event.partnerId);
          market = markets.data.find((p) => p.id === partner?.marketId);
        }

        if (event.unitId) {
          unit = units.find((p: Unit) => p.id === event.unitId);
        }

        const schedulerObj = {
          id,
          date,
          market,
          marketName: market && market.name ? market.name : '',
          unit,
          unitName: unit && unit.name ? unit.name : '',
          partner,
          partnerName: partner && partner.name ? partner.name : '',
          status,
          _createdDate,
          _lastUpdatedDate,
        };
        return schedulerObj;
      });
    const { currentRole, currentUser } = UserModule;
    if (currentRole !== 'fw_manager'
      && currentRole !== 'fw_admin') {
      const eventsList = mappedEvents
        .filter((el: any) => el.unit && el.partner && el.market)
        .filter((e: ISchedulerTableInfo) => {
          if (e.partner?.ownerUserId === currentUser?.id) {
            return e;
          }
          return null;
        });
      return orderBy(eventsList, '_createdDate', 'desc');
    }
    return orderBy(mappedEvents.filter((el: any) => el.unit && el.partner && el.market),
      '_createdDate', 'desc');
  }

  public static async getPartners() {
    const localPartners = localStorage.getItem('partners');
    const items = JSON.parse(localPartners as any);
    if (items) {
      return items;
    }
    const partnersApi = new PartnersApi(AppModule.apiConfig);
    const { data } = await partnersApi.getPartners();
    if (data) {
      localStorage.setItem('partners', JSON.stringify(data));
      return data;
    }
    return null;
  }

  public static async getUnits() {
    const localUnits = localStorage.getItem('units');
    const items = JSON.parse(localUnits as any);
    if (items) {
      return items;
    }
    const unitsApi = new UnitsApi(AppModule.apiConfig);
    const { data } = await unitsApi.getsUnits();
    if (data) {
      localStorage.setItem('units', JSON.stringify(data));
      return data;
    }
    return null;
  }

  public static getDateRangeStats(
    stats: any,
    prevDate: string,
    nextDate: string,
  ) {
    const previousDate = moment(prevDate).format('YYYY-MM-DD');
    const NextDate = moment(nextDate).format('YYYY-MM-DD');
    const statsOnDate = !stats || stats.map((stat: any) => {
      const { _createdDate } = stat;
      const date = moment(_createdDate).format('YYYY-MM-DD');
      if (previousDate && NextDate) {
        if (moment(date).isBetween(previousDate, NextDate)
          || moment(date).isSame(previousDate)
          || moment(date).isSame(NextDate)) {
          return stat;
        }
        return '';
      }
      return '';
    });

    return statsOnDate.filter((el: any) => el !== '');
  }

  public static getDateRangeEvents(
    stats: any,
    prevDate: string,
    nextDate: string,
  ) {
    const previousDate = moment(prevDate).format('YYYY-MM-DD');
    const NextDate = moment(nextDate).format('YYYY-MM-DD');
    const statsOnDate = !stats || stats.map((stat: any) => {
      const { date } = stat;
      const formattedDate = moment(date).format('YYYY-MM-DD');
      if (previousDate && NextDate) {
        if (moment(formattedDate).isBetween(previousDate, NextDate)) {
          return stat;
        }
        return '';
      }
      return '';
    });

    return statsOnDate.filter((el: any) => el !== '');
  }

  public static getDayWiseStats(stats: any, prevDate: string) {
    let filteredStats = [];
    if (stats.length > 0) {
      const previousDate = moment(prevDate).format('YYYY-MM-DD');
      const yesterdayDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
      if (previousDate === yesterdayDate) {
        filteredStats = stats.filter((doc: any) => {
          const { _createdDate } = doc;
          const date = moment(_createdDate).format('YYYY-MM-DD');
          if (date === yesterdayDate) {
            return doc;
          }
          return '';
        });
      } else {
        filteredStats = stats.filter((doc: any) => {
          const { _createdDate } = doc;
          const date = moment(_createdDate).format('YYYY-MM-DD');
          if (date >= previousDate) {
            return doc;
          }
          return '';
        });
      }
    }
    return filteredStats;
  }

  public static getDayWiseEvents(stats: any, prevDate: string) {
    let filteredStats = [];
    if (stats.length > 0) {
      const previousDate = moment(prevDate).format('YYYY-MM-DD');
      const yesterdayDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
      if (previousDate === yesterdayDate) {
        filteredStats = stats.filter((doc: any) => {
          const { date } = doc;
          const formattedDate = moment(date).format('YYYY-MM-DD');
          if (formattedDate === yesterdayDate) {
            return doc;
          }
          return '';
        });
      } else {
        filteredStats = stats.filter((doc: any) => {
          const { date } = doc;
          const formattedDate = moment(date).format('YYYY-MM-DD');
          if (formattedDate >= previousDate) {
            return doc;
          }
          return '';
        });
      }
    }
    return filteredStats;
  }

  /* Market Overview */

  public static getCountMarkets(markets: any, prevDate: string,
    nextDate: string, isDateRange: boolean) {
    const filteredMarkets = this.getFilteredMarketsByDate(compact(markets),
      prevDate, nextDate, isDateRange);
    const mappedMarket: any = {};
    mappedMarket.fetchedMarkets = filteredMarkets;
    return mappedMarket;
  }

  public static getFilteredMarketsByDate(markets: any, prevDate: string,
    nextDate: string, isDateRange: boolean) {
    const marketsOnDate = !markets || markets.map((market: any) => {
      const { units, partners } = market;
      const filteredUnits = this.getFilteredMarketsDate(units, prevDate, nextDate, isDateRange);
      const filteredPartners = this.getFilteredMarketsDate(partners, prevDate,
        nextDate, isDateRange);
      return {
        ...market,
        units: filteredUnits,
        partners: filteredPartners,
      };
    });

    return marketsOnDate;
  }

  public static getFilteredMarketsDate(arr: any, prevDate: string,
    nextDate: string, isDateRange: boolean) {
    const previousDate = moment(prevDate).format('YYYY-MM-DD');
    const NextDate = moment(nextDate).format('YYYY-MM-DD');
    const filteredMarkets = isDateRange
      ? this.getDateRangeStats(arr, previousDate, NextDate)
      : this.getDayWiseStats(arr, previousDate);

    return filteredMarkets.filter((el: any) => el !== '');
  }

  public static getCountCuisines(markets: any, cuisines: any) {
    const mappedCuisines = !cuisines || cuisines.map((cuisine: any) => {
      const filteredPartners = markets
        .filter((m: any) => m.partners
          .some((el: Partner) => el.cuisineTypes?.includes(cuisine.name)));
      // eslint-disable-next-line no-param-reassign
      cuisine.partners = filteredPartners;
      return cuisine;
    });
    const mostPopularCuisines = this.getMostPopularCuisines(mappedCuisines);
    const leastPopularCuisines = this.getLeastPopularCuisines(mappedCuisines);
    const newlyAddedCuisines = this.getNewlyAddedCuisines(mappedCuisines);
    const mappedCuisinesTypes: any = {};
    mappedCuisinesTypes.mostPopularCuisines = mostPopularCuisines;
    mappedCuisinesTypes.leastPopularCuisines = leastPopularCuisines;
    mappedCuisinesTypes.newlyAddedCuisines = newlyAddedCuisines;
    return mappedCuisinesTypes;
  }

  public static getCountCuisinesMarketFiltered(markets: any, cuisines: any) {
    /* eslint-disable no-param-reassign */
    const mappedCuisines = !cuisines || cuisines.map((cuisine: any) => {
      const filteredPartners = markets
        .filter((m: any) => m.partners
          .some((el: Partner) => el.cuisineTypes?.includes(cuisine.name)));
      let { partners } = cuisine;
      partners = filteredPartners;
      cuisine.partners = partners;
      return cuisine;
    });
    const mostPopularCuisines = this.getMostPopularCuisines(mappedCuisines);
    const leastPopularCuisines = this.getLeastPopularCuisines(mappedCuisines);
    const newlyAddedCuisines = this.getNewlyAddedCuisines(cuisines);
    const mappedCuisinesTypes: any = {};
    mappedCuisinesTypes.mostPopularCuisines = mostPopularCuisines;
    mappedCuisinesTypes.leastPopularCuisines = leastPopularCuisines;
    mappedCuisinesTypes.newlyAddedCuisines = newlyAddedCuisines;
    return mappedCuisinesTypes;
  }

  public static getFilteredByDate(array: any, prevDate: string,
    nextDate: string, isDateRange: boolean) {
    const previousDate = moment(prevDate).format('YYYY-MM-DD');
    const NextDate = moment(nextDate).format('YYYY-MM-DD');
    const filteredArray = isDateRange
      ? this.getDateRangeStats(array, previousDate, NextDate)
      : this.getDayWiseStats(array, previousDate);
    return filteredArray;
  }

  public static getMostPopularCuisines(cuisines: any) {
    const arr = JSON.parse(JSON.stringify(cuisines));
    return arr
      .sort((a: any, b: any) => (a.partners.length < b.partners.length ? 1 : -1))
      .splice(0, 10);
  }

  public static getLeastPopularCuisines(cuisines: any) {
    const arr = JSON.parse(JSON.stringify(cuisines));
    return arr
      .sort((a: any, b: any) => (a.partners.length > b.partners.length ? 1 : -1))
      .splice(0, 10);
  }

  public static getNewlyAddedCuisines(cuisines: any) {
    const parsedCuisines = JSON.parse(JSON.stringify(cuisines));
    const date = moment().subtract(7, 'days').format('YYYY-MM-DD');
    const filteredCuisines = !parsedCuisines || parsedCuisines
      .filter((cuisine: any) => {
        let { partners } = cuisine;
        partners = this.getDatewiseStats(partners, date);
        cuisine.partners = partners;
        return cuisine;
      });
    return filteredCuisines;
  }

  public static async getSectorLists(units: Array<Unit>) {
    await SystemModule.fetchSectors();
    const { sectors } = SystemModule;
    const sectArray: any[] = [];
    if (units && sectors) {
      const filteredSectors = !units || units.map((unit: Unit) => {
        const { sectorId } = unit;
        if (sectorId) {
          const sectorName = MarketUtil.getSectorNameByID(sectors, sectorId);
          sectArray.push(sectorName);
          return sectArray;
        }
        return null;
      });
      const updateArr = compact(filteredSectors[0]);
      const unitUniueArray = [...new Set(updateArr)];
      const countValueArray = unitUniueArray.map(
        (value) => [updateArr.filter(str => str === value).length],
      );
      const newCountArr: any[] = [];
      countValueArray.map((v) => newCountArr.push(v.toString()));
      const objSector: any = {};
      objSector.sectorVal = unitUniueArray;
      const result = newCountArr.map(i => Number(i))
        .sort((a: number, b: number) => (a > b ? -1 : 1));
      objSector.sectorCount = result;
      return objSector;
    }
    return null;
  }

  public static async getCountSectorLists(prevDate: string, nextDate: string,
    isDateRange: boolean) {
    const units = await this.getUnits();
    const filteredUnits = isDateRange
      ? this.getDateRangeStats(units, prevDate, nextDate)
      : this.getDayWiseStats(units, prevDate);
    return this.getSectorLists(filteredUnits);
  }

  public static async getSectorListsByMarket(marketIds: Array<string>,
    prevDate: string, nextDate: string, isDateRange: boolean) {
    const units = await this.getUnits();
    const filteredUnits = isDateRange
      ? this.getDateRangeStats(units, prevDate, nextDate)
      : this.getDayWiseStats(units, prevDate);
    const marketFilterUnits = !filteredUnits || filteredUnits
      .map((unit: Unit) => {
        if (unit.marketId && marketIds.includes(unit.marketId)) {
          return unit;
        }
        return null;
      }).filter((el: any) => el);
    return this.getSectorLists(marketFilterUnits);
  }

  public static async getPartnerListsByMarket(marketIds: Array<string>,
    prevDate: string,
    nextDate: string,
    isDateRange: boolean) {
    const partners = await this.getPartners();
    const filteredPartners = isDateRange
      ? this.getDateRangeStats(partners, prevDate, nextDate)
      : this.getDayWiseStats(partners, prevDate);
    const filteredPartnersByMarkets = filteredPartners.filter((p: Partner) => {
      if (p.marketId && marketIds.includes(p.marketId)) {
        return p;
      }
      return null;
    }).filter((el: any) => el);
    return this.getPartnersList(filteredPartnersByMarkets);
  }

  public static async getPartnerCategoryListsByMarket(marketIds: Array<string>,
    prevDate: string,
    nextDate: string,
    isDateRange: boolean) {
    const partners = await this.getPartners();
    const filteredPartners = isDateRange
      ? this.getDateRangeStats(partners, prevDate, nextDate)
      : this.getDayWiseStats(partners, prevDate);
    const filteredPartnersByMarkets = filteredPartners.filter((p: Partner) => {
      if (p.marketId && marketIds.includes(p.marketId)) {
        return p;
      }
      return null;
    }).filter((el: any) => el);
    return this.getPartnerCategoryList(filteredPartnersByMarkets);
  }

  public static async getUnitListsByMarket(marketIds: Array<string>,
    prevDate: string, nextDate: string, isDateRange: boolean) {
    const units = await this.getUnits();

    const filteredUnits = isDateRange
      ? this.getDateRangeStats(units, prevDate, nextDate)
      : this.getDayWiseStats(units, prevDate);

    const filteredMarketUnits = filteredUnits
      .filter((unit: Unit) => {
        if (unit.marketId && marketIds.includes(unit.marketId)) {
          return unit;
        }
        return null;
      }).filter((el: any) => el);

    return this.getUnitsList(filteredMarketUnits);
  }

  public static async getPartnersList(partners: Array<Partner>) {
    if (partners) {
      const activePartners = partners
        .filter((partner: Partner) => partner.active
          && partner.onboarding?.status === 'completed').length;
      const inActivePartners = partners
        .filter((partner: Partner) => !partner.active
          || partner.onboarding?.status !== 'completed').length;
      const objPartners: any = {};
      if (activePartners >= inActivePartners) {
        objPartners.partnerStatus = ['Active', 'In-Active'];
        const result = [activePartners, inActivePartners];
        objPartners.partnersCount = result;
      } else {
        objPartners.partnerStatus = ['In-Active', 'Active'];
        const result = [inActivePartners, activePartners];
        objPartners.partnersCount = result;
      }

      return objPartners;
    }
    return null;
  }

  public static async getCountPartnersList(prevDate: string,
    nextDate: string, isDateRange: boolean) {
    const partners = await this.getPartners();
    const filteredPartners = isDateRange
      ? this.getDateRangeStats(partners, prevDate, nextDate)
      : this.getDayWiseStats(partners, prevDate);
    return this.getPartnersList(filteredPartners);
  }

  public static async getCountUnitsList(prevDate: string,
    nextDate: string, isDateRange: boolean) {
    const units = await this.getUnits();
    const filteredUnits = isDateRange
      ? this.getDateRangeStats(units, prevDate, nextDate)
      : this.getDayWiseStats(units, prevDate);
    return this.getUnitsList(filteredUnits);
  }

  public static getPartnerCategoryList(partners: Array<Partner>) {
    if (partners) {
      const restaurantPartners = partners
        .filter((partner: Partner) => partner.type === 'restaurant').length;
      const foodtruckPartners = partners
        .filter((partner: Partner) => partner.type === 'foodtruck').length;
      const apronPartners = partners
        .filter((partner: Partner) => (partner.type as string) === 'apron_exchange').length;

      const categories = [
        { status: 'Restaurants', count: restaurantPartners },
        { status: 'Foodtrucks', count: foodtruckPartners },
        { status: 'Apron Exchange', count: apronPartners },
      ].sort((a, b) => b.count - a.count);

      const objPartners: any = {};

      objPartners.status = categories.map(c => c.status);
      objPartners.count = categories.map(c => c.count);

      return objPartners;
    }
    return null;
  }

  public static async getCountPartnerCategoryList(prevDate: string,
    nextDate: string, isDateRange: boolean) {
    const partners = await this.getPartners();
    const filteredPartners = isDateRange
      ? this.getDateRangeStats(partners, prevDate, nextDate)
      : this.getDayWiseStats(partners, prevDate);
    return this.getPartnerCategoryList(filteredPartners);
  }

  public static async getUnitsList(units: Array<Unit>) {
    if (units) {
      const activeUnits = units
        .filter((unit: Unit) => unit.active).length;
      const inActiveUnits = units
        .filter((unit: Unit) => !unit.active).length;
      const objUnits: any = {};
      if (activeUnits > inActiveUnits) {
        objUnits.unitStatus = ['Active', 'In-Active'];
        const result = [activeUnits, inActiveUnits];
        objUnits.unitsCount = result;
      } else {
        objUnits.unitStatus = ['In-Active', 'Active'];
        const result = [inActiveUnits, activeUnits];
        objUnits.unitsCount = result;
      }
      return objUnits;
    }
    return null;
  }

  public static async getMappedMarketOverviewMarkets(markets: any):
    Promise<IMarketOverviewTableInfo[]> {
    const partnersData = await this.getPartners();
    const unitsData = await this.getUnits();
    const filteredMarkets = !markets || markets.map((market: any) => {
      const {
        id, name, _createdDate, _lastUpdatedDate,
      } = market;
      const partners = partnersData.filter((p: Partner) => p.marketId === market.id);
      const units = unitsData.filter((u: Unit) => u.marketId === market.id);

      const newlyOnboardedPartners = this.getThirtyDaysMarkets(partners);
      const newlyOnboardedUnits = this.getThirtyDaysMarkets(units);

      const isNew = this.getSevenDaysMarkets(_createdDate);

      const marketOverview = {
        id,
        name,
        units,
        partners,
        newlyOnBoarded: {
          units: newlyOnboardedUnits,
          partners: newlyOnboardedPartners,
        },
        isNew,
        _createdDate,
        _lastUpdatedDate,
      };
      return marketOverview;
    });

    return orderBy(filteredMarkets, '_createdDate', 'desc');
  }

  public static async getMappedMarketOverviewCuisines(cuisines: any, markets: any) {
    const partnerMarkets = await this.getMappedMarketOverviewMarkets(markets);
    const filteredCuisines = !cuisines || cuisines.map((cuisine: any) => {
      const filteredPartners = partnerMarkets
        .filter((m: any) => m.partners.some((el: Partner) => el.cuisineTypes?.includes(cuisine)));
      const marketOverview = {
        name: cuisine,
        partners: filteredPartners,
      };
      return marketOverview;
    });
    return filteredCuisines;
  }

  public static getThirtyDaysMarkets(arr: any) {
    const date = moment().subtract(30, 'days').format('YYYY-MM-DD');
    const partners = this.getDatewiseStats(arr, date);
    return partners;
  }

  public static getSevenDaysMarkets(_createdDate: any) {
    let isNew = false;
    const currentDate = moment().format('YYYY-MM-DD');
    const date = moment().subtract(7, 'days').format('YYYY-MM-DD');
    const createdDate = moment(_createdDate).format('YYYY-MM-DD');
    if (moment(createdDate).isBetween(date, currentDate, null!, '[]')
     || moment(createdDate).isSame(date)
     || moment(createdDate).isSame(currentDate)) { isNew = true; }
    return isNew;
  }
}
